import type { Document, DocumentMetadata } from '@/services/documentVault';

/**
 * Read-only label/value rows for vault metadata (shared by viewer, details sheet, etc.).
 */
export function getDocumentMetadataRows(
  document: Document,
  formatDate: (d: Date) => string
): { label: string; value: string }[] {
  if (!document.metadata) return [];

  const meta = document.metadata as unknown as Record<string, unknown>;
  const fields: { label: string; value: string }[] = [];

  const fmtDateMaybe = (v: unknown) => {
    if (v instanceof Date) return formatDate(v);
    const d = new Date(v as string);
    return Number.isNaN(d.getTime()) ? String(v) : formatDate(d);
  };

  const pushIf = (label: string, value: unknown, format?: (v: unknown) => string) => {
    if (value === undefined || value === null || value === '') return;
    fields.push({ label, value: format ? format(value) : String(value) });
  };

  switch (document.type) {
    case 'passport':
      pushIf('Passport Number', meta.number);
      pushIf('Issuing Country', meta.issuingCountry);
      pushIf('Issue Date', meta.issueDate, fmtDateMaybe);
      pushIf('Expiration Date', meta.expirationDate, fmtDateMaybe);
      pushIf('Nationality', meta.nationality);
      pushIf('Place of Birth', meta.placeOfBirth);
      pushIf('Full Name', meta.fullName);
      break;
    case 'driving_license':
      pushIf('License Number', meta.number);
      pushIf('Issuing State', meta.issuingState);
      pushIf('Issuing Country', meta.issuingCountry);
      pushIf('Issue Date', meta.issueDate, fmtDateMaybe);
      pushIf('Expiration Date', meta.expirationDate, fmtDateMaybe);
      pushIf('License Class', meta.licenseClass);
      pushIf('Address', meta.address);
      break;
    case 'visa':
      pushIf('Visa Number', meta.number);
      pushIf('Issuing Country', meta.issuingCountry);
      pushIf('Issue Date', meta.issueDate, fmtDateMaybe);
      pushIf('Expiration Date', meta.expirationDate, fmtDateMaybe);
      pushIf('Visa Type', meta.visaType);
      pushIf('Entry Date', meta.entryDate, fmtDateMaybe);
      pushIf('Exit Date', meta.exitDate, fmtDateMaybe);
      pushIf('Number of Entries', meta.numberOfEntries);
      break;
    case 'health_record':
      pushIf('Record Type', meta.recordType);
      pushIf('Provider', meta.providerName);
      pushIf('Date', meta.date, fmtDateMaybe);
      pushIf('Doctor', meta.doctorName);
      pushIf('Medical Record Number', meta.medicalRecordNumber);
      pushIf('Diagnosis', meta.diagnosis);
      pushIf('Notes', meta.notes);
      break;
    case 'insurance':
      pushIf('Policy Number', meta.policyNumber);
      pushIf('Provider', meta.provider);
      pushIf('Coverage Start', meta.coverageStartDate, fmtDateMaybe);
      pushIf('Coverage End', meta.coverageEndDate, fmtDateMaybe);
      pushIf('Policy Type', meta.policyType);
      pushIf('Coverage Details', meta.coverageDetails);
      pushIf('Contact Info', meta.contactInfo);
      break;
    case 'other':
    default:
      pushIf('Document Number', meta.documentNumber ?? meta.number);
      pushIf('Issuing Authority', meta.issuingAuthority ?? meta.issuingCountry);
      pushIf('Issue Date', meta.issueDate, fmtDateMaybe);
      pushIf('Expiration Date', meta.expirationDate, fmtDateMaybe);
      pushIf('Notes', meta.notes);
      break;
  }

  return fields;
}

export function documentHasMetadataContent(metadata: DocumentMetadata | undefined): boolean {
  if (!metadata) return false;
  return Object.values(metadata as unknown as Record<string, unknown>).some(
    (v) => v !== undefined && v !== null && v !== ''
  );
}
